import { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';

import { DocsLayout } from '../components/docs/DocsLayout';
import type { DocsTocLink } from '../components/docs/DocsTableOfContents';
import {
  Callout,
  CodeBlock,
  DocH1,
  DocH2,
  DocH3,
  DocHr,
  DocOl,
  DocP,
  DocSubtitle,
  DocTable,
  DocUl,
  InlineCode,
  linkClass,
} from '../components/docs/docsTypography';
import { usePageMeta } from '../hooks/usePageMeta';
import { getPublicPageMeta } from '../seo/publicPageMeta';
import { trackPageView } from '../services/telemetry';
import { getApiHost } from '../utils/getApiHost';

const TOC_LINKS: readonly DocsTocLink[] = [
  { href: '#getting-started', label: 'Getting Started' },
  { href: '#generate-your-api-key', label: 'Generate Your API Key' },
  { href: '#making-api-requests', label: 'Making API Requests' },
  { href: '#inputs-and-outputs', label: 'Inputs & Outputs' },
  { href: '#midi', label: 'MIDI Input & Output' },
  { href: '#photos', label: 'Photos & Page Images' },
  { href: '#existing-fingerings', label: 'Existing Fingerings' },
  { href: '#webhook-integration', label: 'Webhook Integration' },
  { href: '#checking-job-status', label: 'Checking Job Status' },
  { href: '#list-recent-jobs', label: 'List Recent Jobs' },
  { href: '#trial-and-credits', label: 'Trial & Credits' },
  { href: '#billing-subscription', label: 'Billing & Subscription' },
  { href: '#license', label: 'License & AI Training' },
  { href: '#api-reference', label: 'API Reference' },
  { href: '#changelog', label: 'Changelog' },
  { href: '#support', label: 'Support' },
];

// Typography primitives, TOC nav, and the two-column shell now live
// under `src/components/docs/` — see the imports above.

// JSON-LD structured data for the API documentation page. Combines a
// `TechArticle` (so search engines can treat the page as a substantive
// article rather than a generic landing page), an `APIReference`
// (specifically signals that this page documents a REST API, which
// improves AI-chatbot ingestion and may unlock developer-doc rich
// results), and a `BreadcrumbList` (gives `Home > Business API` in
// SERP breadcrumbs).
const API_DOC_STRUCTURED_DATA = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': ['TechArticle', 'APIReference'],
      '@id': 'https://pineapplemusiclab.com/business-api#article',
      headline: 'PineappleMusicLab API Integration Guide',
      name: 'PineappleMusicLab API',
      description:
        'Business API integration guide for PineappleMusicLab — covers API key generation, the asynchronous fingering generation endpoint for MusicXML, PDF, photo and MIDI input, MusicXML and MIDI output, webhook delivery, job status polling, trials and credits, billing, and a complete endpoint, status, and error-code reference.',
      url: 'https://pineapplemusiclab.com/business-api',
      inLanguage: 'en',
      isAccessibleForFree: true,
      audience: {
        '@type': 'Audience',
        audienceType: 'Developers',
      },
      proficiencyLevel: 'Beginner',
      programmingLanguage: ['cURL', 'HTTP', 'Python'],
      programmingModel: 'REST',
      targetPlatform: 'Web API',
      keywords: [
        'piano fingering API',
        'MusicXML API',
        'MIDI fingering API',
        'sheet music OCR API',
        'REST API',
        'webhooks',
        'asynchronous job processing',
        'sheet music processing',
      ],
      about: [
        {
          '@type': 'Thing',
          name: 'Piano fingering generation API',
        },
        {
          '@type': 'Thing',
          name: 'Asynchronous job processing with webhooks',
        },
      ],
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': 'https://pineapplemusiclab.com/business-api',
      },
      publisher: {
        '@type': 'Organization',
        name: 'PineappleMusicLab',
        url: 'https://pineapplemusiclab.com/',
      },
    },
    {
      '@type': 'BreadcrumbList',
      '@id': 'https://pineapplemusiclab.com/business-api#breadcrumbs',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: 'https://pineapplemusiclab.com/',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Business API',
          item: 'https://pineapplemusiclab.com/business-api',
        },
      ],
    },
  ],
};

function BusinessApiDocumentation() {
  const apiBase = useMemo(() => getApiHost(), []);
  const businessRoot = `${apiBase}/api/v1/business`;

  usePageMeta(getPublicPageMeta('/business-api'));

  useEffect(() => {
    trackPageView('BusinessApiDocumentation', '/business-api');
  }, []);

  const curlGenerateMusicXml = `curl -X POST "${businessRoot}/fingering/generate?webhook_url=https://your-server.com/webhook&webhook_secret=your-secret" \\
  -H "X-API-Key: your-api-key-here" \\
  -F "file=@/path/to/your/score.xml"`;

  const curlGeneratePdf = `curl -X POST "${businessRoot}/fingering/generate?webhook_url=https://your-server.com/webhook&webhook_secret=your-secret" \\
  -H "X-API-Key: your-api-key-here" \\
  -F "file=@/path/to/your/score.pdf"`;

  const curlGenerateMidi = `curl -X POST "${businessRoot}/fingering/generate?webhook_url=https://your-server.com/webhook&midi_hands=both" \\
  -H "X-API-Key: your-api-key-here" \\
  -F "file=@/path/to/your/song.mid"`;

  const curlGenerateScoreToMidi = `curl -X POST "${businessRoot}/fingering/generate?webhook_url=https://your-server.com/webhook&output_format=midi" \\
  -H "X-API-Key: your-api-key-here" \\
  -F "file=@/path/to/your/score.pdf"`;

  const curlGeneratePhotos = `curl -X POST "${businessRoot}/fingering/generate?webhook_url=https://your-server.com/webhook" \\
  -H "X-API-Key: your-api-key-here" \\
  -F "file=@/path/to/page1.jpg" \\
  -F "file=@/path/to/page2.jpg"`;

  const pythonGenerate = `import requests

API_KEY = "your-api-key-here"
BASE = "${businessRoot}"

with open("score.pdf", "rb") as f:
    response = requests.post(
        f"{BASE}/fingering/generate",
        headers={"X-API-Key": API_KEY},
        params={
            "webhook_url": "https://your-server.com/webhook",
            "existing_fingering": "keep",  # or "ignore"
            "output_format": "musicxml",   # or "midi"
        },
        files={"file": f},
    )
response.raise_for_status()
job_id = response.json()["job_id"]`;

  const pythonPoll = `import time
import requests

while True:
    job = requests.get(
        f"{BASE}/fingering/status/{job_id}",
        headers={"X-API-Key": API_KEY},
    ).json()
    if job["status"] in ("completed", "failed"):
        break
    time.sleep(10)  # please poll no faster than every 10 seconds

if job["status"] == "completed":
    result = requests.get(job["result"]["download_url"])
    open("output." + ("mid" if job["result"]["output_format"] == "midi" else "xml"), "wb").write(result.content)`;

  const pythonReadMidi = `import mido

mid = mido.MidiFile("output.mid")
for track in mid.tracks:
    for msg in track:
        if msg.type == "polytouch":
            # msg.note: the note's MIDI pitch, msg.value: finger 1-5
            # It directly follows that note's note_on, at the same tick.
            print(track.name, msg.channel, msg.note, "finger", msg.value)`;

  const pythonStripFingerings = `# Remove fingering events before playback, if your synth reacts to aftertouch
for track in mid.tracks:
    kept, carried = [], 0
    for msg in track:
        if msg.type == "polytouch":
            carried += msg.time
            continue
        kept.append(msg.copy(time=msg.time + carried))
        carried = 0
    track[:] = kept`;

  const curlAccount = `curl "${businessRoot}/account" \\
  -H "X-API-Key: your-api-key-here"`;

  const curlStatus = `curl "${businessRoot}/fingering/status/abc123-def456-ghi789" \\
  -H "X-API-Key: your-api-key-here"`;

  const curlJobs = `curl "${businessRoot}/fingering/jobs?limit=20" \\
  -H "X-API-Key: your-api-key-here"`;

  return (
    <>
      {/* JSON-LD: TechArticle + APIReference + BreadcrumbList. Helps
          search engines and AI chatbots understand this page as a
          developer-facing API reference rather than a generic page. */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(API_DOC_STRUCTURED_DATA),
        }}
      />

      <DocsLayout
        tocLinks={TOC_LINKS}
        header={
          <>
            <DocH1>PineappleMusicLab API</DocH1>
            <DocSubtitle>API Integration Guide</DocSubtitle>
          </>
        }
      >
        <DocP>
          Welcome to the PineappleMusicLab Business API! This guide will help
          you integrate our piano fingering generation service into your
          application.
        </DocP>

        <DocHr />

        <section aria-labelledby="getting-started">
          <DocH2 id="getting-started">Getting Started</DocH2>

          <DocH3>Step 1: Create an Account</DocH3>
          <DocOl>
            <li>
              Sign up at our website (
              <a
                href="https://pineapplemusiclab.com"
                className={linkClass}
                target="_blank"
                rel="noopener noreferrer"
              >
                pineapplemusiclab.com
              </a>
              )
            </li>
            <li>
              Contact us at{' '}
              <a
                href="mailto:hello@pineapplemusiclab.com"
                className={linkClass}
              >
                hello@pineapplemusiclab.com
              </a>{' '}
              with the email you used to sign up
            </li>
            <li>We&apos;ll discuss pricing and set up a trial period</li>
            <li>
              Once approved, your account will be upgraded to a{' '}
              <strong>business account</strong>
            </li>
          </DocOl>

          <DocH3>Step 2: Prepare Your Webhook Endpoint</DocH3>
          <DocP>
            Webhooks are the <strong>recommended</strong> way to receive
            fingering generation results in production. Prepare a webhook
            endpoint on your server. Your endpoint should:
          </DocP>
          <DocUl>
            <li>
              Accept <InlineCode>POST</InlineCode> requests
            </li>
            <li>
              Accept <InlineCode>application/json</InlineCode> content type
            </li>
            <li>
              Return a <InlineCode>2xx</InlineCode> status code on success
            </li>
          </DocUl>
          <Callout>
            <span className="font-medium">Quick test tip:</span> If you just
            want to try the API and don&apos;t have a server ready, point{' '}
            <InlineCode>webhook_url</InlineCode> at a unique URL from{' '}
            <a
              href="https://webhook.site"
              className={linkClass}
              target="_blank"
              rel="noopener noreferrer"
            >
              webhook.site
            </a>{' '}
            &mdash; you can inspect incoming webhooks in your browser with no
            server code. We recommend this over{' '}
            <a href="#checking-job-status" className={linkClass}>
              polling the status endpoint
            </a>
            ; if you do poll, please keep the interval to at least 10 seconds
            per job.
          </Callout>
        </section>

        <DocHr />

        <section aria-labelledby="generate-your-api-key">
          <DocH2 id="generate-your-api-key">Generate Your API Key</DocH2>
          <DocP>
            Once your business account is activated, generate your API key
            through the web interface:
          </DocP>
          <DocOl>
            <li>
              Log in to{' '}
              <a
                href="https://pineapplemusiclab.com"
                className={linkClass}
                target="_blank"
                rel="noopener noreferrer"
              >
                pineapplemusiclab.com
              </a>
            </li>
            <li>
              Open the account menu and choose <strong>My Profile</strong> (
              <Link to="/profile" className={linkClass}>
                on-site link
              </Link>
              )
            </li>
            <li>
              In the <strong>API Key</strong> section, click{' '}
              <strong>Generate API Key</strong>
            </li>
            <li>Copy the key and store it somewhere secure</li>
          </DocOl>
          <Callout>
            <span className="font-medium">Important:</span> API keys are shown
            only once. If you lose yours, generating a new key will invalidate
            the previous one.
          </Callout>
        </section>

        <DocHr />

        <section aria-labelledby="making-api-requests">
          <DocH2 id="making-api-requests">Making API Requests</DocH2>

          <DocH3>Generate Fingering</DocH3>
          <DocP>
            Submit a <strong>MusicXML, PDF, photo, or MIDI</strong> file to
            generate piano fingerings. The processing is{' '}
            <strong>asynchronous</strong> — results are delivered to your
            webhook URL.
          </DocP>
          <DocP>
            <strong>Note:</strong> PDFs and photos are routed through our OMR
            (optical music recognition) pipeline first. Results come back as
            MusicXML or MIDI — see{' '}
            <a href="#inputs-and-outputs" className={linkClass}>
              Inputs &amp; Outputs
            </a>
            .
          </DocP>

          <DocH3>Endpoint</DocH3>
          <CodeBlock>{`POST ${businessRoot}/fingering/generate`}</CodeBlock>

          <DocH3>Authentication</DocH3>
          <DocP>
            Include your API key in the <InlineCode>X-API-Key</InlineCode>{' '}
            header.
          </DocP>

          <DocH3>Request parameters</DocH3>
          <DocTable
            headers={['Parameter', 'Type', 'Required', 'Description']}
            rows={[
              [
                <InlineCode key="f">file</InlineCode>,
                'File',
                'Yes',
                <>
                  MusicXML (<InlineCode>.xml</InlineCode>,{' '}
                  <InlineCode>.musicxml</InlineCode>,{' '}
                  <InlineCode>.mxl</InlineCode>), PDF (
                  <InlineCode>.pdf</InlineCode>), MIDI (
                  <InlineCode>.mid</InlineCode>, <InlineCode>.midi</InlineCode>
                  ), or page images (<InlineCode>.jpg</InlineCode>,{' '}
                  <InlineCode>.jpeg</InlineCode>, <InlineCode>.png</InlineCode>,{' '}
                  <InlineCode>.webp</InlineCode>). For a multi-page photo
                  upload, repeat <InlineCode>file</InlineCode> once per page, in
                  page order.
                </>,
              ],
              [
                <InlineCode key="w">webhook_url</InlineCode>,
                'Query string',
                'Yes*',
                'URL to receive results. *Optional if a default webhook URL is set on your account.',
              ],
              [
                <InlineCode key="s">webhook_secret</InlineCode>,
                'Query string',
                'No',
                'Secret for webhook authentication',
              ],
              [
                <InlineCode key="ef">existing_fingering</InlineCode>,
                <>
                  <InlineCode>keep</InlineCode> |{' '}
                  <InlineCode>ignore</InlineCode>
                </>,
                'No',
                <>
                  Default <InlineCode>keep</InlineCode>. What to do with
                  fingerings already in the score — see{' '}
                  <a href="#existing-fingerings" className={linkClass}>
                    Existing Fingerings
                  </a>
                  . Not applied to MIDI input.
                </>,
              ],
              [
                <InlineCode key="of">output_format</InlineCode>,
                <>
                  <InlineCode>musicxml</InlineCode> |{' '}
                  <InlineCode>midi</InlineCode>
                </>,
                'No',
                <>
                  Defaults to <InlineCode>midi</InlineCode> for MIDI input and{' '}
                  <InlineCode>musicxml</InlineCode> otherwise. MIDI input only
                  supports <InlineCode>midi</InlineCode>.
                </>,
              ],
              [
                <InlineCode key="mh">midi_hands</InlineCode>,
                <>
                  <InlineCode>right</InlineCode> | <InlineCode>left</InlineCode>{' '}
                  | <InlineCode>both</InlineCode>
                </>,
                'No',
                <>
                  Default <InlineCode>both</InlineCode>. MIDI output only: which
                  hand tracks to write (<InlineCode>both</InlineCode> = two
                  tracks).
                </>,
              ],
              [
                <InlineCode key="ih">input_hand</InlineCode>,
                <>
                  <InlineCode>right</InlineCode> | <InlineCode>left</InlineCode>
                </>,
                'Sometimes',
                <>
                  Which hand a <strong>single-staff score</strong> or a{' '}
                  <strong>single-track MIDI file</strong> is for. Required for
                  single-track MIDI; without it, single-staff systems in a score
                  are skipped. (<InlineCode>hand</InlineCode> is accepted as an
                  older name.)
                </>,
              ],
            ]}
          />

          <DocH3>Request example (MusicXML)</DocH3>
          <CodeBlock>{curlGenerateMusicXml}</CodeBlock>

          <DocH3>Request example (PDF)</DocH3>
          <CodeBlock>{curlGeneratePdf}</CodeBlock>

          <DocH3>Request example (Python)</DocH3>
          <CodeBlock>{pythonGenerate}</CodeBlock>

          <DocH3>Response</DocH3>
          <CodeBlock>{`{
  "job_id": "abc123-def456-ghi789",
  "status": "accepted",
  "message": "Job accepted. Results will be delivered to your webhook URL."
}`}</CodeBlock>
          <DocP>
            The <InlineCode>job_id</InlineCode> can be used to check the job
            status later.
          </DocP>
        </section>

        <DocHr />

        <section aria-labelledby="inputs-and-outputs">
          <DocH2 id="inputs-and-outputs">Inputs &amp; Outputs</DocH2>
          <DocTable
            headers={['Input', 'Output (default)', 'Other output', 'Notes']}
            rows={[
              [
                'MusicXML / MXL',
                'MusicXML',
                'MIDI',
                'Fingerings added as <technical><fingering>, one per note.',
              ],
              [
                'PDF',
                'MusicXML',
                'MIDI',
                'Transcribed by OMR first. The original PDF is not returned.',
              ],
              [
                'Photos / page images',
                'MusicXML',
                'MIDI',
                'Pages are straightened before OMR. See Photos & Page Images.',
              ],
              [
                'MIDI',
                'MIDI',
                '—',
                'Fingered directly from the notes, no transcription step.',
              ],
            ]}
          />
          <DocUl>
            <li>
              <strong>MusicXML output:</strong> every fingered note carries a{' '}
              <InlineCode>{'<notations><technical><fingering>'}</InlineCode>{' '}
              element with a single finger number 1–5.
            </li>
            <li>
              <strong>MIDI output:</strong> fingerings are written as polyphonic
              aftertouch events — see{' '}
              <a href="#midi" className={linkClass}>
                MIDI Input &amp; Output
              </a>
              .
            </li>
            <li>
              PDFs and photos take longer than MusicXML or MIDI, since the page
              must be transcribed before fingerings are generated.
            </li>
            <li>
              Every output file carries a license notice — see{' '}
              <a href="#license" className={linkClass}>
                License &amp; AI Training
              </a>
              .
            </li>
          </DocUl>

          <DocH3>Request example (score to MIDI)</DocH3>
          <CodeBlock>{curlGenerateScoreToMidi}</CodeBlock>
        </section>

        <DocHr />

        <section aria-labelledby="midi">
          <DocH2 id="midi">MIDI Input &amp; Output</DocH2>

          <DocH3>Input: which track is which hand</DocH3>
          <DocUl>
            <li>
              <strong>2 tracks with notes:</strong> the first is the right hand,
              the second the left hand. Tracks with only tempo or other meta
              events are not counted.
            </li>
            <li>
              <strong>1 track with notes:</strong> set{' '}
              <InlineCode>input_hand=right</InlineCode> or{' '}
              <InlineCode>input_hand=left</InlineCode>. Without it the request
              is rejected with <InlineCode>400</InlineCode>.
            </li>
            <li>
              <strong>3 or more tracks with notes</strong>, or a file with no
              notes, is rejected with <InlineCode>400</InlineCode>. The check
              runs before the job is created, so a rejected file uses no credit.
            </li>
            <li>
              Recorded performances work: timing is read in seconds, and notes
              that start within ~20 ms of each other are treated as a chord.
            </li>
            <li>
              MIDI input is always fingered from scratch;{' '}
              <InlineCode>existing_fingering</InlineCode> does not apply.
            </li>
          </DocUl>

          <DocH3>Request example (MIDI)</DocH3>
          <CodeBlock>{curlGenerateMidi}</CodeBlock>

          <DocH3>Output: how fingerings are encoded</DocH3>
          <DocP>
            Each fingered note-on is followed, on the same tick and channel, by
            a <strong>polyphonic aftertouch</strong> event (status{' '}
            <InlineCode>0xA0</InlineCode>) whose note number is the note&apos;s
            pitch and whose value is the finger (1 = thumb … 5 = little finger).
            Because the event names its note, chord fingerings are never
            ambiguous. Notes without a fingering get no event.
          </DocP>
          <DocUl>
            <li>
              <strong>MIDI input:</strong> your file comes back with every
              original event kept, plus the fingering events. Any poly
              aftertouch already on the note tracks is replaced.{' '}
              <InlineCode>midi_hands=right</InlineCode> or{' '}
              <InlineCode>left</InlineCode> drops the other hand&apos;s track.
            </li>
            <li>
              <strong>Score input with</strong>{' '}
              <InlineCode>output_format=midi</InlineCode>: a type-1 file with a
              tempo track, then <InlineCode>Piano RH</InlineCode> on channel{' '}
              <InlineCode>0</InlineCode> and <InlineCode>Piano LH</InlineCode>{' '}
              on channel <InlineCode>1</InlineCode> (shown as channels 1 and 2
              in most music software), as selected by{' '}
              <InlineCode>midi_hands</InlineCode>. Repeats are not expanded.
            </li>
          </DocUl>

          <DocH3>Reading fingerings (Python)</DocH3>
          <CodeBlock>{pythonReadMidi}</CodeBlock>
          <Callout>
            <span className="font-medium">Playback:</span> most piano sounds
            ignore poly aftertouch, but some synths use it for vibrato or
            volume. If yours does, strip the events before playback:
          </Callout>
          <CodeBlock>{pythonStripFingerings}</CodeBlock>
        </section>

        <DocHr />

        <section aria-labelledby="photos">
          <DocH2 id="photos">Photos &amp; Page Images</DocH2>
          <DocP>
            Send phone photos or scans as <InlineCode>.jpg</InlineCode>,{' '}
            <InlineCode>.jpeg</InlineCode>, <InlineCode>.png</InlineCode>, or{' '}
            <InlineCode>.webp</InlineCode>. Repeat the{' '}
            <InlineCode>file</InlineCode> field once per page, in page order.
            Pages are straightened (curved and tilted pages are flattened)
            before transcription, the same as in our app.
          </DocP>
          <DocUl>
            <li>Up to 15 pages per job, up to 10 MB per page.</li>
            <li>
              HEIC photos (the iPhone default) are not accepted; export them as
              JPG or PNG first.
            </li>
            <li>
              For best results: one page per photo, the whole page in frame,
              even lighting, and no strong shadows across the staff.
            </li>
          </DocUl>

          <DocH3>Request example (photos)</DocH3>
          <CodeBlock>{curlGeneratePhotos}</CodeBlock>
        </section>

        <DocHr />

        <section aria-labelledby="existing-fingerings">
          <DocH2 id="existing-fingerings">Existing Fingerings</DocH2>
          <DocP>
            Scores often already have some fingerings, printed by the editor or
            added by a teacher. <InlineCode>existing_fingering</InlineCode>{' '}
            controls what happens to them:
          </DocP>
          <DocUl>
            <li>
              <InlineCode>keep</InlineCode> (default): existing fingerings stay
              exactly as they are, and the model chooses the remaining
              fingerings to fit around them. For PDFs and photos, this applies
              to fingerings printed on the page and recognized by OMR.
            </li>
            <li>
              <InlineCode>ignore</InlineCode>: existing fingerings are removed
              and every note is fingered fresh.
            </li>
          </DocUl>
        </section>

        <DocHr />

        <section aria-labelledby="webhook-integration">
          <DocH2 id="webhook-integration">Webhook Integration</DocH2>
          <DocP>
            When fingering generation completes, we&apos;ll send a{' '}
            <InlineCode>POST</InlineCode> request to your webhook URL. This is
            the recommended integration path for production.
          </DocP>
          <Callout>
            <span className="font-medium">Testing webhooks:</span> For quick
            experiments, point <InlineCode>webhook_url</InlineCode> at a
            temporary URL generated by{' '}
            <a
              href="https://webhook.site"
              className={linkClass}
              target="_blank"
              rel="noopener noreferrer"
            >
              webhook.site
            </a>
            . You can inspect headers and the JSON payload directly in your
            browser without writing any server code.
          </Callout>

          <DocH3>Webhook headers</DocH3>
          <DocTable
            headers={['Header', 'Description']}
            rows={[
              [
                <InlineCode key="ct">Content-Type</InlineCode>,
                'application/json',
              ],
              [
                <InlineCode key="jid">X-Job-Id</InlineCode>,
                'The job identifier',
              ],
              [
                <InlineCode key="ev">X-Webhook-Event</InlineCode>,
                <InlineCode key="evv">fingering.completed</InlineCode>,
              ],
              [
                <InlineCode key="auth">Authorization</InlineCode>,
                <>
                  <InlineCode>Bearer {'{webhook_secret}'}</InlineCode> (only if
                  webhook secret is configured)
                </>,
              ],
            ]}
          />

          <DocH3>Success payload</DocH3>
          <CodeBlock>{`{
  "event": "fingering.completed",
  "job_id": "abc123-def456-ghi789",
  "success": true,
  "created_at": "2026-01-29T12:00:00Z",
  "completed_at": "2026-01-29T12:00:15Z",
  "result": {
    "download_url": "https://storage.blob.core.windows.net/...",
    "original_filename": "your-score.xml",
    "output_format": "musicxml",
    "license": "Licensed Pineapple Music Lab output. ..."
  }
}`}</CodeBlock>

          <DocH3>Error payload</DocH3>
          <CodeBlock>{`{
  "event": "fingering.completed",
  "job_id": "abc123-def456-ghi789",
  "success": false,
  "created_at": "2026-01-29T12:00:00Z",
  "completed_at": "2026-01-29T12:00:05Z",
  "error": {
    "message": "Invalid MusicXML format"
  }
}`}</CodeBlock>

          <DocH3>Downloading results</DocH3>
          <DocP>
            The <InlineCode>download_url</InlineCode> in the success payload is
            a <strong>temporary pre-signed URL</strong> to download the
            processed MusicXML or MIDI file (see{' '}
            <InlineCode>output_format</InlineCode>) with fingering annotations.
            Download the file promptly as the URL expires.
          </DocP>
          <DocP>
            The <InlineCode>original_filename</InlineCode> field reflects the
            file you uploaded (e.g. <InlineCode>score.pdf</InlineCode>
            ), so for PDF and photo inputs it won&apos;t match the file you
            actually download. For a multi-page photo upload it is the first
            page&apos;s name.
          </DocP>

          <DocH3>Webhook retry policy</DocH3>
          <DocP>
            If your webhook endpoint returns a non-2xx status code or times out:
          </DocP>
          <DocUl>
            <li>
              We attempt delivery up to <strong>5 times</strong> in total (1
              initial attempt + 4 retries)
            </li>
            <li>
              Retries use exponential backoff: 1s → 2s → 4s → 8s between
              attempts
            </li>
            <li>Maximum delay between attempts: 60 seconds</li>
          </DocUl>
        </section>

        <DocHr />

        <section aria-labelledby="checking-job-status">
          <DocH2 id="checking-job-status">Checking Job Status</DocH2>
          <DocP>
            You can query the status of a job at any time. When the job has
            completed, the response also includes a{' '}
            <InlineCode>result.download_url</InlineCode>, so polling alone is
            enough to retrieve the output without setting up a webhook receiver.
            This is the simplest way for trial users to try the API end-to-end.
          </DocP>

          <DocH3>Endpoint</DocH3>
          <CodeBlock>{`GET ${businessRoot}/fingering/status/{job_id}`}</CodeBlock>

          <DocH3>Authentication</DocH3>
          <DocP>
            Include your API key in the <InlineCode>X-API-Key</InlineCode>{' '}
            header.
          </DocP>

          <DocH3>Request example</DocH3>
          <CodeBlock>{curlStatus}</CodeBlock>

          <DocH3>Response (in progress)</DocH3>
          <CodeBlock>{`{
  "job_id": "abc123-def456-ghi789",
  "status": "processing",
  "created_at": "2026-01-29T12:00:00Z",
  "completed_at": null,
  "webhook_delivery_status": "pending",
  "webhook_delivery_attempts": 0,
  "error_message": null,
  "result": null
}`}</CodeBlock>

          <DocH3>Response (completed)</DocH3>
          <CodeBlock>{`{
  "job_id": "abc123-def456-ghi789",
  "status": "completed",
  "created_at": "2026-01-29T12:00:00Z",
  "completed_at": "2026-01-29T12:00:15Z",
  "webhook_delivery_status": "delivered",
  "webhook_delivery_attempts": 1,
  "error_message": null,
  "result": {
    "download_url": "https://storage.blob.core.windows.net/...",
    "original_filename": "your-score.xml",
    "output_format": "musicxml",
    "license": "Licensed Pineapple Music Lab output. ..."
  }
}`}</CodeBlock>
          <DocP>
            The <InlineCode>download_url</InlineCode> returned here is a freshly
            minted, temporary pre-signed URL pointing at the same output file
            that webhooks deliver. Download the file promptly &mdash; the URL
            expires.
          </DocP>

          <DocH3>Polling example (Python)</DocH3>
          <CodeBlock>{pythonPoll}</CodeBlock>

          <DocH3>Webhooks vs. polling</DocH3>
          <DocUl>
            <li>
              <strong>Production:</strong> use webhooks. They avoid polling
              overhead entirely and deliver results as soon as they&apos;re
              ready.
            </li>
            <li>
              <strong>Testing / trial:</strong> the easiest setup is a webhook
              receiver on{' '}
              <a
                href="https://webhook.site"
                className={linkClass}
                target="_blank"
                rel="noopener noreferrer"
              >
                webhook.site
              </a>
              . You get a unique URL with no code, and can inspect the payload
              in your browser.
            </li>
            <li>
              If you do poll for testing, please use an interval of{' '}
              <strong>at least 10 seconds</strong> per job.
            </li>
          </DocUl>

          <DocH3>Job status values</DocH3>
          <DocTable
            headers={['Status', 'Description']}
            rows={[
              [
                <InlineCode key="a">accepted</InlineCode>,
                'Job accepted, queued for processing',
              ],
              [
                <InlineCode key="u">uploaded</InlineCode>,
                'File uploaded to storage',
              ],
              [
                <InlineCode key="p">processing</InlineCode>,
                'Job is being processed',
              ],
              [
                <InlineCode key="c">completed</InlineCode>,
                'Fingering generation finished successfully',
              ],
              [
                <InlineCode key="f">failed</InlineCode>,
                'Job failed (see error_message)',
              ],
            ]}
          />

          <DocH3>Webhook delivery status values</DocH3>
          <DocTable
            headers={['Status', 'Description']}
            rows={[
              [
                <InlineCode key="pe">pending</InlineCode>,
                'Delivery not yet attempted',
              ],
              [
                <InlineCode key="ip">in_progress</InlineCode>,
                'Delivery in progress',
              ],
              [
                <InlineCode key="de">delivered</InlineCode>,
                'Successfully delivered',
              ],
              [
                <InlineCode key="fa">failed</InlineCode>,
                'Delivery failed after all retries',
              ],
            ]}
          />
        </section>

        <DocHr />

        <section aria-labelledby="list-recent-jobs">
          <DocH2 id="list-recent-jobs">List Recent Jobs</DocH2>
          <DocP>Retrieve a list of your recent jobs.</DocP>

          <DocH3>Endpoint</DocH3>
          <CodeBlock>{`GET ${businessRoot}/fingering/jobs`}</CodeBlock>

          <DocH3>Authentication</DocH3>
          <DocP>
            Include your API key in the <InlineCode>X-API-Key</InlineCode>{' '}
            header.
          </DocP>

          <DocH3>Query parameters</DocH3>
          <DocTable
            headers={['Parameter', 'Type', 'Default', 'Description']}
            rows={[
              [
                <InlineCode key="l">limit</InlineCode>,
                'int',
                '50',
                'Maximum number of jobs to return (1–100)',
              ],
            ]}
          />

          <DocH3>Request example</DocH3>
          <CodeBlock>{curlJobs}</CodeBlock>

          <DocH3>Response</DocH3>
          <CodeBlock>{`{
  "jobs": [
    {
      "job_id": "abc123-def456-ghi789",
      "status": "completed",
      "original_filename": "score.xml",
      "created_at": "2026-01-29T12:00:00Z",
      "completed_at": "2026-01-29T12:00:15Z",
      "webhook_delivery_status": "delivered"
    }
  ],
  "count": 1
}`}</CodeBlock>
        </section>

        <DocHr />

        <section aria-labelledby="trial-and-credits">
          <DocH2 id="trial-and-credits">Trial &amp; Credits</DocH2>
          <DocP>
            Trial accounts have an end date, and some accounts have a credit
            limit. <strong>One credit is one job</strong>, whatever the number
            of pages. A credit is used when a job is accepted and given back if
            the job fails.
          </DocP>
          <DocUl>
            <li>
              After the trial ends, new jobs are rejected with{' '}
              <InlineCode>403</InlineCode> and{' '}
              <InlineCode>{'"reason": "trial_expired"'}</InlineCode>.
            </li>
            <li>
              When the credit limit is used up, new jobs are rejected with{' '}
              <InlineCode>402</InlineCode> and{' '}
              <InlineCode>{'"reason": "credit_limit_reached"'}</InlineCode>.
            </li>
            <li>
              Jobs already accepted always finish, and status and downloads keep
              working.
            </li>
          </DocUl>

          <DocH3>Endpoint</DocH3>
          <CodeBlock>{`GET ${businessRoot}/account`}</CodeBlock>

          <DocH3>Request example</DocH3>
          <CodeBlock>{curlAccount}</CodeBlock>

          <DocH3>Response</DocH3>
          <CodeBlock>{`{
  "trial_ends_at": "2026-12-01T00:00:00+00:00",
  "trial_active": true,
  "credit_limit": 100,
  "credits_used": 12,
  "credits_remaining": 88
}`}</CodeBlock>
          <DocP>
            <InlineCode>null</InlineCode> means no trial end date or no limit.
            Your trial end date and remaining credits are also shown on your
            profile page.
          </DocP>
        </section>

        <DocHr />

        <section aria-labelledby="billing-subscription">
          <DocH2 id="billing-subscription">Billing &amp; Subscription</DocH2>

          <DocH3>Monthly subscription</DocH3>
          <DocOl>
            <li>
              We&apos;ll discuss and create a monthly subscription for you based
              on your needs
            </li>
            <li>You&apos;ll receive an invoice via email</li>
            <li>
              <strong>Base subscription:</strong> Fixed monthly rate
            </li>
            <li>
              <strong>Usage billing:</strong> Additional charges for requests
              exceeding the included quota (billed monthly based on previous
              month&apos;s usage)
            </li>
          </DocOl>

          <DocH3>Annual plan option</DocH3>
          <DocUl>
            <li>
              Pay annual rate upfront on the first day of service, with
              discussed discount
            </li>
            <li>Extra usage billed monthly</li>
          </DocUl>

          <DocH3>Managing billing</DocH3>
          <DocP>
            Contact us directly at{' '}
            <a href="mailto:hello@pineapplemusiclab.com" className={linkClass}>
              hello@pineapplemusiclab.com
            </a>
            .
          </DocP>
        </section>

        <DocHr />

        <section aria-labelledby="license">
          <DocH2 id="license">License &amp; AI Training</DocH2>
          <DocP>
            You may use the files you receive inside your own product, under
            your agreement with us and our{' '}
            <Link to="/terms-of-service" className={linkClass}>
              Terms of Service
            </Link>
            . Output files, and the fingering annotations in them, may{' '}
            <strong>not</strong> be used to train, fine-tune, distill, or
            evaluate machine-learning models that compete with
            PineappleMusicLab.
          </DocP>
        </section>

        <DocHr />

        <section aria-labelledby="api-reference">
          <DocH2 id="api-reference">API Reference</DocH2>

          <DocH3>Base URL</DocH3>
          <CodeBlock>{apiBase}</CodeBlock>

          <DocH3>Endpoints summary</DocH3>
          <DocTable
            headers={['Method', 'Endpoint', 'Description']}
            rows={[
              [
                <InlineCode key="po">POST</InlineCode>,
                <InlineCode key="p1">
                  /api/v1/business/fingering/generate
                </InlineCode>,
                'Submit file for processing',
              ],
              [
                <InlineCode key="ge">GET</InlineCode>,
                <InlineCode key="g1">
                  {'/api/v1/business/fingering/status/{job_id}'}
                </InlineCode>,
                'Check job status',
              ],
              [
                <InlineCode key="g2">GET</InlineCode>,
                <InlineCode key="g3">
                  /api/v1/business/fingering/jobs
                </InlineCode>,
                'List recent jobs',
              ],
              [
                <InlineCode key="g4">GET</InlineCode>,
                <InlineCode key="g5">/api/v1/business/account</InlineCode>,
                'Trial end date and credit usage',
              ],
            ]}
          />

          <DocH3>Authentication</DocH3>
          <DocP>
            All API endpoints require your API key in the{' '}
            <InlineCode>X-API-Key</InlineCode> header:
          </DocP>
          <CodeBlock>{'X-API-Key: your-api-key-here'}</CodeBlock>

          <DocH3>Supported file types</DocH3>
          <DocTable
            headers={['Extension', 'MIME type', 'Notes']}
            rows={[
              [
                <InlineCode key="x1">.xml</InlineCode>,
                <InlineCode key="m1">application/xml</InlineCode>,
                'MusicXML',
              ],
              [
                <InlineCode key="x2">.musicxml</InlineCode>,
                <InlineCode key="m2">
                  application/vnd.recordare.musicxml+xml
                </InlineCode>,
                'MusicXML',
              ],
              [
                <InlineCode key="x3">.mxl</InlineCode>,
                <InlineCode key="m3">
                  application/vnd.recordare.musicxml
                </InlineCode>,
                'Compressed MusicXML',
              ],
              [
                <InlineCode key="x4">.pdf</InlineCode>,
                <InlineCode key="m4">application/pdf</InlineCode>,
                'Routed through OMR; output is MusicXML or MIDI',
              ],
              [
                <InlineCode key="x5">.mid, .midi</InlineCode>,
                <InlineCode key="m5">audio/midi</InlineCode>,
                'Up to 2 tracks with notes; output is MIDI',
              ],
              [
                <InlineCode key="x6">.jpg, .jpeg, .png, .webp</InlineCode>,
                <InlineCode key="m6">image/*</InlineCode>,
                'Photos or scans, up to 15 pages; output is MusicXML or MIDI',
              ],
            ]}
          />

          <DocH3>Error responses</DocH3>
          <DocP>All error responses follow this format:</DocP>
          <CodeBlock>{`{
  "detail": "Error message describing what went wrong",
  "reason": "credit_limit_reached"
}`}</CodeBlock>
          <DocP>
            <InlineCode>reason</InlineCode> is only present on some errors (
            <InlineCode>trial_expired</InlineCode>,{' '}
            <InlineCode>credit_limit_reached</InlineCode>).
          </DocP>

          <DocH3>Common HTTP status codes</DocH3>
          <DocTable
            headers={['Code', 'Description']}
            rows={[
              [<InlineCode key="200">200</InlineCode>, 'Success'],
              [
                <InlineCode key="202">202</InlineCode>,
                'Accepted (job submitted)',
              ],
              [
                <InlineCode key="400">400</InlineCode>,
                'Bad request (invalid input)',
              ],
              [
                <InlineCode key="401">401</InlineCode>,
                'Unauthorized (missing/invalid API key)',
              ],
              [<InlineCode key="402">402</InlineCode>, 'Credit limit reached'],
              [
                <InlineCode key="403">403</InlineCode>,
                'Trial ended, or the API key does not belong to a business account',
              ],
              [<InlineCode key="404">404</InlineCode>, 'Not found'],
              [
                <InlineCode key="422">422</InlineCode>,
                'Request is missing a required field (e.g. no file attached)',
              ],
              [
                <InlineCode key="502">502</InlineCode>,
                'Processing service unavailable; try again later',
              ],
              [<InlineCode key="500">500</InlineCode>, 'Server error'],
            ]}
          />
        </section>

        <DocHr />

        <section aria-labelledby="changelog">
          <DocH2 id="changelog">Changelog</DocH2>
          <DocH3>November 2026</DocH3>
          <DocP>
            Existing requests keep working unchanged; everything new is opt-in,
            except the items marked <strong>changed</strong>.
          </DocP>
          <DocUl>
            <li>
              <strong>New:</strong> MIDI input and MIDI output (
              <InlineCode>output_format</InlineCode>,{' '}
              <InlineCode>midi_hands</InlineCode>,{' '}
              <InlineCode>input_hand</InlineCode>).
            </li>
            <li>
              <strong>New:</strong> photo and page-image input, including
              multi-page uploads.
            </li>
            <li>
              <strong>New:</strong> <InlineCode>existing_fingering</InlineCode>{' '}
              parameter.
            </li>
            <li>
              <strong>New:</strong> <InlineCode>GET /account</InlineCode>, and
              the <InlineCode>402</InlineCode> / <InlineCode>403</InlineCode>{' '}
              trial and credit errors.
            </li>
            <li>
              <strong>New:</strong>{' '}
              <InlineCode>result.output_format</InlineCode> and{' '}
              <InlineCode>result.license</InlineCode> in webhook and status
              responses.
            </li>
            <li>
              <strong>Changed:</strong> output files now carry a license notice
              (see{' '}
              <a href="#license" className={linkClass}>
                License &amp; AI Training
              </a>
              ). Notes and fingerings are unchanged.
            </li>
            <li>
              <strong>Changed:</strong> for PDF input, fingerings printed on the
              page are now kept by default, and a note no longer gets two{' '}
              <InlineCode>{'<fingering>'}</InlineCode> elements. Pass{' '}
              <InlineCode>existing_fingering=ignore</InlineCode> for fresh
              fingerings.
            </li>
          </DocUl>
        </section>

        <DocHr />

        <section aria-labelledby="support">
          <DocH2 id="support">Support</DocH2>
          <DocP>
            For questions, issues, or to discuss pricing, please contact our
            team at:
          </DocP>
          <DocP>
            <a href="mailto:hello@pineapplemusiclab.com" className={linkClass}>
              hello@pineapplemusiclab.com
            </a>
          </DocP>
        </section>

        <p className="mt-12 text-sm text-stone-500">
          Last updated: November 2026
        </p>
      </DocsLayout>
    </>
  );
}

export { BusinessApiDocumentation };
